import { verifyDocument as verifyDocumentAPI } from 'api/documents'
import { addExpenseTypeRule } from 'api/expense'
import { Field } from 'components/DocumentDetail/types'
import { GLOBAL_PROFILE } from 'components/Root.loader'
import { ERROR_TEXT } from 'config'
import { dateFormat } from 'utils/format'
import { splitName } from 'utils/highlights'

import { BaseDocument } from './BaseDocuments'
import { DocumentInvoiceV2JSON } from './DocumentsInvoiceV2'

interface ExpenseTypeChangedItem {
  title: string
  item: string
  code: string
}

export abstract class BaseInvoiceDocument extends BaseDocument {
  static get MAX_CUSTOM_FIELD_ID() {
    return 20
  }
  constructor(public docJSON: DocumentInvoiceV2JSON) {
    super()
  }

  get ocrResp() {
    return this.docJSON.ocrResp
  }
  get ocrEditResult() {
    return this.docJSON.ocrEditResult
  }
  get config() {
    return this.docJSON.config
  }

  // date 2 string if its typs is Date
  protected date2str(field: Date) {
    if (field === null) return ''
    if (field instanceof Date) {
      return !isNaN(field.getTime()) ? dateFormat(field) : ''
    }
    return field
  }

  protected abstract fixDates(newEdit: { [x: string]: any })

  async addExpenseTypeRules(edit: { [x: string]: any }) {
    const { ocrResp, ocrEditResult } = this.docJSON
    const changed: ExpenseTypeChangedItem[] = []

    ocrResp.itemLines?.forEach((respItem) => {
      const original = ocrEditResult.itemLines?.find((orgItem) => orgItem.id === respItem.id)
      const edited = edit.itemLines && edit.itemLines.find((edItem) => edItem.id === respItem.id)

      if (original?.expenseTypeCode !== edited?.expenseTypeCode && edited?.expenseTypeCode) {
        // edited row can be deleted
        changed.push({
          title: edit.headerBase.invoiceTitle,
          item: respItem.itemName.text,
          code: edited.expenseTypeCode,
        })
      }
    })
    await this.callAddExpenseTypeRule(changed)
  }

  async callAddExpenseTypeRule(changed: ExpenseTypeChangedItem[]) {
    const { ocrResp } = this.docJSON
    if (changed.length > 0) {
      await addExpenseTypeRule({
        userGroup: GLOBAL_PROFILE.profile.userGroup,
        add: changed.map(({ title, item, code }) => ({
          company: ocrResp.headerBillFrom.billFromCompanyName.text,
          title,
          item,
          code,
        })),
      })
    }
  }

  async save(edit: { [x: string]: any }) {
    const newEdit = structuredClone(edit)
    this.addExpenseTypeRules(newEdit)
    this.ocrEditResult.itemLines = edit.itemLines // delete line can't be merged with es-toolkit merge
    this.fixDates(newEdit)
    return super.save(newEdit)
  }

  async confirm(edit: { [x: string]: any }) {
    this.addExpenseTypeRules(edit)
    this.fixDates(edit)
    await super.confirm(edit)
  }

  async verify(edit: { [x: string]: any }) {
    const changed: ExpenseTypeChangedItem[] = []
    const doc = this.docJSON.verifyData!.verifyData.map(
      ({ fieldName, id, user2Data, user3Data }) => {
        const [category, field] = splitName(fieldName)
        let verifyInput = edit[category]?.[field]
        if (fieldName.match(/^itemLines/) && id) {
          verifyInput = edit[id]?.[field]

          if (field === 'expenseTypeCode') {
            const lastUserData = user3Data || user2Data
            if (lastUserData !== verifyInput && verifyInput) {
              const ocrRespLine = this.docJSON.ocrResp.itemLines?.find(
                ({ id: ocrRespId }) => id === ocrRespId,
              )
              if (ocrRespLine) {
                changed.push({
                  title: this.docJSON.ocrEditResult.headerBase.invoiceTitle,
                  item: ocrRespLine.itemName.text,
                  code: verifyInput,
                })
              }
            }
          }
        }

        return {
          fieldName,
          id,
          data: verifyInput.toString(),
        }
      },
    )
    return [await this.callAddExpenseTypeRule(changed), await verifyDocumentAPI(this.objectID, doc)]
  }

  canSend() {
    if (this.isVerify) {
      if (!this.isFinished || !(GLOBAL_PROFILE.isManager || GLOBAL_PROFILE.isRegister)) {
        return false
      }
    }
    return GLOBAL_PROFILE.userGroupConfig.useConcur
  }

  getLabel(section: string, fieldName: string) {
    return LABELS[section][fieldName]
  }
}

const customLabels = Object.fromEntries(
  Array.from({ length: BaseInvoiceDocument.MAX_CUSTOM_FIELD_ID }, (_, i) => [
    `custom${i + 1}`,
    `カスタム${i + 1}`,
  ]),
)

export const LABELS: { [section: string]: Record<string, string> } = {
  headerBase: {
    invoiceDate: '請求書発行日',
    invoiceNo: '請求書番号',
    invoiceTitle: '件名',
    totalAmountTaxIn: '合計金額(税込)',
    totalAmountTaxEx: '合計金額(税別)',
    totalTax: '税金額',
    taxInfo: '税金情報',
    totalPrice8: '小計金額(税込8%)',
    totalPrice10: '小計金額(税込10%)',
    totalPrice: '小計金額(税込)',
    subTotalPrice8: '小計金額(税別8%)',
    subTotalPrice10: '小計金額(税別10%)',
    subTotalPrice: '小計金額(税別)',
    taxAmount8: '小計税額(8%)',
    taxAmount10: '小計税額(10%)',
    taxAmount: '小計税額',
    taxAmountInfo: '小計税金情報',
    carryoverAmount: '繰越額',
    lastBilledAmount: '前回請求額',
    depositAmount: '入金額',
    discount: '値引額',
    withholdingTax: '源泉徴収税（支払済）',
    etcAmount: 'その他金額',
    closingDate: '締日',
    otherDate: 'その他の日付情報',
    paymentDueDate: '支払期限',
    currencyN: '通貨',
    currency: '通貨単位',
    orderNoOfSlip: '発注番号',
    countryCode: '国コード',
  },
  headerBillFrom: {
    billFromCompanyName: '請求元会社名',
    billFromCompanyId: '請求元事業者登録番号',
    billFromDepartment: '請求元担当部署',
    billFromManagerName: '請求元担当者名',
    billFromPersonName: '請求元代表者',
    billFromPostalCode: '請求元郵便番号',
    billFromAddress: '請求元住所',
    billFromEmail: '請求元電子メール',
    billFromTel: '請求元TEL',
    billFromFax: '請求元FAX',
    billFromUrl: '請求元HP',
    vendorCode: '請求元企業コード',
    billFromAddressCode: 'Addressコード',
    ...customLabels,
  },
  headerBillTo: {
    billToCompanyName: '請求先会社名',
    billToDept: '請求先担当部署',
    billToPerson: '請求先担当者名',
    billToPostalCode: '請求先郵便番号',
    billToAddress: '請求先住所',
    billToTel: '請求先TEL',
    billToFax: '請求先FAX',
  },
  headerBank: {
    bankName: '振込先銀行名',
    bankCode: '振込先銀行コード',
    bankBranchName: '振込先銀行支店名',
    bankBranchCode: '振込先銀行支店コード',
    bankSwiftCode: '振込先SWIFTコード',
    bankAccountHolder: '振込先口座名義',
    bankAccountType: '振込先口座種別',
    bankAccountCode: '振込先口座番号',
  },
  headerContact: {
    department: '問合せ先担当部署',
    personName: '問合せ先担当者',
    postalCode: '問合せ先郵便番号',
    address: '問合せ先住所',
    email: '問合せ先電子メール',
    tel: '問合せ先TEL',
    fax: '問合せ先FAX',
    homepage: '問合せ先HP',
  },
  other: {
    note: '備考',
  },
  itemLines: {
    itemName: '品目',
    itemUnitPriceTaxIn: '単価（税込）',
    itemUnitPrice: '単価（税別）',
    itemQuantity: '個数',
    itemUnitOfQuantity: '数量単位',
    itemAmountTaxIn: '価格（税込）',
    itemAmountTaxEx: '価格（税別）',
    itemTax: '税金額',
    itemTaxType: '税区分',
    itemDiscount: '値引額',
    itemCurrency: '通貨単位',
    itemDate: '取引日',
    itemOrderNo: '発注番号',
    itemEtc: 'その他',
    expenseTypeCode: '科目',
    expenseTypeCodeDumb: '科目コード',
  },
}

export const HEADER_BILL_FROM = [
  {
    name: 'billFromCompanyName',
    testid: 'bill-from-company-name',
  },
  {
    name: 'billFromCompanyId',
    testid: 'bill-from-company-id',
    rules: {
      validate: (v: string) => {
        if (!isUseConcur()) {
          const regex = /^T\d{13}$/
          if (v !== '' && !regex.test(v)) {
            return ERROR_TEXT.noMatchedCompanyId
          }
        }
      },
      maxLength: 14,
    },
  },
  {
    name: 'billFromDepartment',
    testid: 'bill-from-department',
  },
  {
    name: 'billFromManagerName',
    testid: 'bill-from-manager-name',
  },
  {
    name: 'billFromPersonName',
    testid: 'bill-from-person-name',
  },
  {
    name: 'billFromPostalCode',
    testid: 'bill-from-postal-code',
  },
  {
    name: 'billFromAddress',
    testid: 'bill-from-address',
  },
  {
    name: 'billFromEmail',
    testid: 'bill-from-email',
  },
  {
    name: 'billFromTel',
    testid: 'bill-from-tel',
  },
  {
    name: 'billFromFax',
    testid: 'bill-from-fax',
  },
  {
    name: 'billFromUrl',
    testid: 'bill-from-url',
  },
  {
    name: 'vendorCode',
    testid: 'vendor-code',
    rules: {
      required: ERROR_TEXT.noMatchedVendor,
    },
  },
  {
    name: 'billFromAddressCode',
    testid: 'bill-from-address-code',
    rules: {
      required: true,
    },
  },
].concat(
  new Array(BaseInvoiceDocument.MAX_CUSTOM_FIELD_ID).fill(null).map((_, idx) => ({
    name: `custom${idx + 1}`,
    testid: `custom${idx + 1}`,
  })),
)

export const HEADER_BILL_FROM_KEYS = HEADER_BILL_FROM.map(({ name }) => name)

const isUseConcur = () => {
  return GLOBAL_PROFILE.userGroupConfig.useConcur
}

export const FIELDS: { [section: string]: Field[] } = {
  headerBase: [
    {
      name: 'invoiceDate',
      type: 'date',
      testid: 'invoice-date',
      rules: {
        required: true, // required mark is displayed by this flag.
        validate: (v: Date | string) => {
          const v1 = v instanceof Date ? v : new Date(v)
          // error msg is '' becaouse error message is displayed by datepicker
          return isNaN(v1.getTime()) ? /* ERROR_TEXT.dateFormat */ '' : undefined
        },
      },
    },
    {
      name: 'invoiceNo',
      type: 'text',
      testid: 'invoice-no',
      rules: {
        required: true,
        maxLength: 50,
      },
    },
    {
      name: 'invoiceTitle',
      type: 'text',
      testid: 'invoice-title',
      rules: {
        required: true,
        maxLength: 100,
      },
    },
    {
      name: 'totalAmountTaxIn',
      type: 'number',
      testid: 'total-amount-tax-in',
    },
    {
      name: 'totalAmountTaxEx',
      type: 'number',
      testid: 'total-amount-tax-ex',
    },
    {
      name: 'totalTax',
      type: 'number',
      testid: 'total-tax',
    },
    {
      name: 'taxInfo',
      type: 'text',
      testid: 'tax-info',
    },
    {
      name: 'totalPrice8',
      type: 'number',
      testid: 'total-price8',
    },
    {
      name: 'totalPrice10',
      type: 'number',
      testid: 'total-price10',
    },
    {
      name: 'totalPrice',
      type: 'number',
      testid: 'total-price',
    },
    {
      name: 'subTotalPrice8',
      type: 'number',
      testid: 'sub-total-price8',
    },
    {
      name: 'subTotalPrice10',
      type: 'number',
      testid: 'sub-total-price10',
    },
    {
      name: 'subTotalPrice',
      type: 'number',
      testid: 'sub-total-price',
    },
    {
      name: 'taxAmount8',
      type: 'number',
      testid: 'tax-amount8',
    },
    {
      name: 'taxAmount10',
      type: 'number',
      testid: 'tax-amount10',
    },
    {
      name: 'taxAmount',
      type: 'number',
      testid: 'tax-amount',
    },
    {
      name: 'taxAmountInfo',
      testid: 'tax-amount-info',
    },
    {
      name: 'carryoverAmount',
      type: 'number',
      testid: 'carryover-amount',
    },
    {
      name: 'lastBilledAmount',
      type: 'number',
      testid: 'last-billed-amount',
    },
    {
      name: 'depositAmount',
      type: 'number',
      testid: 'deposit-amount',
    },
    {
      name: 'discount',
      type: 'number',
      testid: 'discount',
    },
    {
      name: 'withholdingTax',
      type: 'number',
      testid: 'withholding-tax',
    },
    {
      name: 'etcAmount',
      type: 'number',
      testid: 'etc-amount',
    },
    {
      name: 'closingDate',
      type: 'date',
      testid: 'closing-date',
      rules: {
        /* required: false, */ // not required but date type is checked
        validate: (v: Date | string) => {
          if (v instanceof Date) {
            // Dateの場合、画面から操作した
            return isNaN(v.getTime()) ? /* ERROR_TEXT.dateFormat */ '' : undefined
          }
        },
      },
    },
    {
      name: 'otherDate',
      type: 'date',
      testid: 'other-date',
      rules: {
        /* required: false, */ // not required but date type is checked
        validate: (v: Date | string) => {
          if (v instanceof Date) {
            // Dateの場合、画面から操作した
            return isNaN(v.getTime()) ? /* ERROR_TEXT.dateFormat */ '' : undefined
          }
        },
      },
    },
    {
      name: 'paymentDueDate',
      type: 'date',
      testid: 'payment-due-date',
      rules: {
        /* required: false, */ // not required but date type is checked
        validate: (v: Date | string) => {
          if (v instanceof Date) {
            // Dateの場合、画面から操作した
            return isNaN(v.getTime()) ? /* ERROR_TEXT.dateFormat */ '' : undefined
          }
        },
      },
    },
    {
      name: 'currencyN',
      testid: 'currency-n',
      rules: {
        required: true,
      },
      type: 'select',
      compProps: {
        options: [
          { label: 'JPY', value: 'JPY' },
          { label: 'USD', value: 'USD' },
        ],
      },
    },
    {
      name: 'currency',
      testid: 'currency',
    },
    {
      name: 'orderNoOfSlip',
      testid: 'order-no-of-slip',
    },
    {
      name: 'countryCode',
      type: 'text',
      testid: 'country-code',
      rules: {
        required: true,
      },
    },
  ],
  headerBillFrom: HEADER_BILL_FROM,
  headerBillTo: [
    {
      name: 'billToCompanyName',
      testid: 'bill-to-company-name',
    },
    {
      name: 'billToDept',
      testid: 'bill-to-dept',
    },
    {
      name: 'billToPerson',
      testid: 'bill-to-person',
    },
    {
      name: 'billToPostalCode',
      testid: 'bill-to-postal-code',
    },
    {
      name: 'billToAddress',
      testid: 'bill-to-address',
    },
    {
      name: 'billToTel',
      testid: 'bill-to-tel',
    },
    {
      name: 'billToFax',
      testid: 'bill-to-fax',
    },
  ],
  headerBank: [
    {
      name: 'bankName',
      testid: 'bank-name',
    },
    {
      name: 'bankCode',
      testid: 'bank-code',
    },
    {
      name: 'bankBranchName',
      testid: 'bank-branch-name',
    },
    {
      name: 'bankBranchCode',
      testid: 'bank-branch-code',
    },
    {
      name: 'bankSwiftCode',
      testid: 'bank-swift-code',
    },
    {
      name: 'bankAccountHolder',
      testid: 'bank-account-holder',
    },
    {
      name: 'bankAccountType',
      testid: 'bank-account-type',
    },
    {
      name: 'bankAccountCode',
      testid: 'bank-account-code',
    },
  ],
  headerContact: [
    {
      name: 'department',
      testid: 'department',
    },
    {
      name: 'personName',
      testid: 'person-name',
    },
    {
      name: 'postalCode',
      testid: 'postal-code',
    },
    {
      name: 'address',
      testid: 'address',
    },
    {
      name: 'email',
      testid: 'email',
    },
    {
      name: 'tel',
      testid: 'tel',
    },
    {
      name: 'fax',
      testid: 'fax',
    },
    {
      name: 'homepage',
      testid: 'homepage',
    },
  ],
  other: [
    {
      name: 'note',
      type: 'text',
      testid: 'note',
      compProps: { multiline: true },
    },
  ],
  itemLine: [
    {
      name: 'itemName',
      type: 'text',
      testid: 'item-name',
      rules: {
        required: true,
      },
    },
    {
      name: 'expenseTypeCode',
      type: 'select-expense',
      testid: 'expense-type-code',
      compProps: {
        options: [], // filled lazily
        style: { maxWidth: '300px', fontSize: '1rem' },
      },
    },
    {
      name: 'expenseTypeCodeDumb',
      type: 'text',
      testid: 'expense-type-code-dumb',
    },
    {
      name: 'itemUnitPriceTaxIn',
      type: 'number',
      testid: 'item-unit-price-tax-in',
    },
    {
      name: 'itemUnitPrice',
      type: 'number',
      testid: 'item-unit-price',
    },
    {
      name: 'itemQuantity',
      type: 'number',
      testid: 'item-quantity',
    },
    {
      name: 'itemUnitOfQuantity',
      testid: 'item-unit-of-quantity',
    },
    {
      name: 'itemAmountTaxIn',
      type: 'number',
      testid: 'item-amount-tax-in',
    },
    {
      name: 'itemAmountTaxEx',
      type: 'number',
      testid: 'item-amount-tax-ex',
    },
    {
      name: 'itemTax',
      type: 'number',
      testid: 'item-tax',
    },
    {
      name: 'itemTaxType',
      testid: 'item-tax-type',
    },
    {
      name: 'itemDiscount',
      type: 'number',
      testid: 'item-discount',
    },
    {
      name: 'itemCurrency',
      testid: 'item-currency',
    },
    {
      name: 'itemDate',
      type: 'date',
      testid: 'item-date',
      rules: {
        /* required: false, */ // not required but date type is checked
        validate: (v: Date | string) => {
          if (v instanceof Date) {
            // Dateの場合、画面から操作した
            return isNaN(v.getTime()) ? /* ERROR_TEXT.dateFormat */ '' : undefined
          }
        },
      },
    },
    {
      name: 'itemOrderNo',
      testid: 'item-order-no',
    },
    {
      name: 'itemEtc',
      testid: 'item-etc',
    },
  ],
}
